import React, { useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const OnomatopoeiaCalculator = () => {
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    setError('');
  };

  const analyzeOnomatopoeia = () => {
    if (!inputText.trim()) {
      setError('Please enter some text');
      return;
    }

    // Clean and prepare the text
    const cleanedText = inputText.toLowerCase()
      .replace(/[^a-z\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .trim();

    const words = cleanedText.split(' ');
    
    // Generate a list of potential onomatopoeic words based on patterns
    const onomatopoeicWords = new Set([
      'bang', 'crash', 'buzz', 'hiss', 'clang', 'whoosh', 'whisper', 'scream', 'thud',
      'roar', 'moo', 'meow', 'quack', 'oink', 'cluck', 'neigh', 'bark', 'growl', 'howl',
      'grr', 'hiss', 'buzz', 'purr', 'mew', 'quack', 'ribbit', 'cockadoodledoo', 'cluck',
      'baa', 'moo', 'oink', 'neigh', 'roar', 'growl', 'howl', 'grr', 'hiss', 'buzz',
      'purr', 'mew', 'quack', 'ribbit', 'cockadoodledoo', 'cluck', 'baa', 'moo', 'oink',
      'neigh', 'roar', 'growl', 'howl', 'grr', 'hiss', 'buzz', 'purr', 'mew', 'quack',
      'ribbit', 'cockadoodledoo', 'cluck', 'baa', 'moo', 'oink', 'neigh', 'roar', 'growl',
      'howl', 'grr', 'hiss', 'buzz', 'purr', 'mew', 'quack', 'ribbit', 'cockadoodledoo',
      'cluck', 'baa', 'moo', 'oink', 'neigh', 'roar', 'growl', 'howl', 'grr', 'hiss',
      'buzz', 'purr', 'mew', 'quack', 'ribbit', 'cockadoodledoo', 'cluck', 'baa', 'moo',
      'oink', 'neigh', 'roar', 'growl', 'howl', 'grr', 'hiss', 'buzz', 'purr', 'mew',
      'quack', 'ribbit', 'cockadoodledoo', 'cluck', 'baa', 'moo', 'oink', 'neigh', 'roar',
      'growl', 'howl', 'grr', 'hiss', 'buzz', 'purr', 'mew', 'quack', 'ribbit', 'cockadoodledoo'
    ]);

    const wordCounts = {};

    words.forEach(word => {
      if (onomatopoeicWords.has(word)) {
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      }
    });

    if (Object.keys(wordCounts).length === 0) {
      setError('No onomatopoeic words found in the input text. Try using more descriptive sound words!');
      return;
    }

    const totalWords = words.length;
    const onomatopoeicFrequency = Object.values(wordCounts).reduce((a, b) => a + b, 0) / totalWords;

    const resultsData = {
      onomatopoeicWords: wordCounts,
      onomatopoeicFrequency: onomatopoeicFrequency.toFixed(3)
    };

    setResults(resultsData);

    const chartData = {
      bar: {
        labels: Object.keys(wordCounts),
        datasets: [{
          label: 'Frequency',
          data: Object.values(wordCounts),
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)',
            'rgba(255, 206, 86, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)',
            'rgba(255, 206, 86, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 206, 86, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: Object.keys(wordCounts),
        datasets: [{
          data: Object.values(wordCounts),
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(255, 206, 86, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(255, 206, 86, 1)'
          ],
          borderWidth: 1
        }]
      }
    };

    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Onomatopoeia Analysis Calculator | Analyze Sound Words in Text</title>
        <meta name="description" content="Analyze onomatopoeic words in text with our calculator. Identify and visualize sound words like buzz, hiss, and bang in your writing." />
        <meta name="keywords" content="onomatopoeia calculator, sound words, text analysis, writing analysis, language analysis" />
        <meta property="og:title" content="Onomatopoeia Analysis Calculator | Analyze Sound Words in Text" />
        <meta property="og:description" content="Analyze onomatopoeic words in text with our calculator. Identify and visualize sound words like buzz, hiss, and bang in your writing." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/onomatopoeia-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🗣️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Onomatopoeia Analysis Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Analyze Onomatopoeic Words in Text</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator analyzes the frequency of onomatopoeic words in a given text.
        </p>
      </div>

      <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="inputText">
            Enter Text:
          </label>
          <textarea
            id="inputText"
            name="inputText"
            value={inputText}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your text here"
            rows="4"
          />
        </div>

        <button
          onClick={analyzeOnomatopoeia}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Onomatopoeia
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Onomatopoeic Words</h4>
                <div className="space-y-2">
                  {Object.entries(results.onomatopoeicWords).map(([word, count]) => (
                    <p key={word} className="flex justify-between">
                      <span>{word}</span>
                      <span className="font-medium">{count}</span>
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Onomatopoeic Frequency</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Frequency</span>
                    <span className="font-medium">{results.onomatopoeicFrequency}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Frequency'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Onomatopoeic Words'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Onomatopoeic Word Frequencies'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Pie Chart</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Onomatopoeic Word Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="how-to-use-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Use</h2>
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 1: Enter Text</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">Type or paste any text in the input box.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">The text can be from any source - books, articles, or your own writing.</p>
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 2: Analyze Onomatopoeia</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">Click the "Analyze Onomatopoeia" button to process the text.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">The calculator will identify and analyze onomatopoeic words in your text.</p>
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 3: View Results</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">View the list of onomatopoeic words found in your text.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">Check the frequency of each onomatopoeic word.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">3.</span>
                <p className="text-gray-700">Explore the bar and pie charts showing word distributions.</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="faq-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
        <div className="space-y-6">
          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">What is Onomatopoeia?</h3>
            <p className="text-gray-700">
              Onomatopoeia is a word that phonetically imitates, resembles, or suggests the sound that it describes. Examples include "buzz", "hiss", "bang", and "crash".
            </p>
          </div>

          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">How does this calculator work?</h3>
            <p className="text-gray-700">
              The calculator analyzes your text to find words that phonetically represent sounds. It then calculates the frequency of these words and displays them in easy-to-understand charts.
            </p>
          </div>

          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">What types of onomatopoeic words does it detect?</h3>
            <p className="text-gray-700">
              The calculator detects a wide range of onomatopoeic words, including animal sounds (meow, moo, quack), environmental sounds (bang, crash, whoosh), and natural sounds (buzz, hiss).
            </p>
          </div>

          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">Can I use this for any text?</h3>
            <p className="text-gray-700">
              Yes! You can use this calculator for any text - from books and articles to your own writing. It works best with descriptive texts that use sound words.
            </p>
          </div>

          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">What do the charts show?</h3>
            <p className="text-gray-700">
              The bar chart shows the frequency of each onomatopoeic word, while the pie chart shows the distribution of these words in your text.
            </p>
          </div>

          <div className="faq-item bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-2">How can I improve my results?</h3>
            <p className="text-gray-700">
              To get better results, try using more descriptive sound words in your text. The calculator works best with texts that contain various onomatopoeic words.
            </p>
          </div>
        </div>
      </div>

      <div className="benefits-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-blue-600 mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">Visual Analysis</h3>
            <p className="text-gray-700">Get clear, visual representations of onomatopoeic word usage through interactive charts.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-green-600 mb-4">📚</div>
            <h3 className="text-xl font-semibold mb-2">Language Enhancement</h3>
            <p className="text-gray-700">Improve your writing by understanding and using onomatopoeic words effectively.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-purple-600 mb-4">💡</div>
            <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
            <p className="text-gray-700">Perfect for students, writers, and anyone interested in the power of sound words in language.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-yellow-600 mb-4">🔄</div>
            <h3 className="text-xl font-semibold mb-2">Dynamic Analysis</h3>
            <p className="text-gray-700">Analyze any text instantly and get unique insights about onomatopoeic word usage.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-red-600 mb-4">🎯</div>
            <h3 className="text-xl font-semibold mb-2">Comprehensive Insights</h3>
            <p className="text-gray-700">Get detailed information about onomatopoeic word frequency and distribution in your text.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-indigo-600 mb-4">🔄</div>
            <h3 className="text-xl font-semibold mb-2">Easy to Use</h3>
            <p className="text-gray-700">Simple interface that anyone can use - just enter text and get instant results.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnomatopoeiaCalculator;